import { create } from 'zustand';
import { supabase } from '../supabase';
import { useAuthStore } from './useAuthStore';
import { useOfflineQueue } from './useOfflineQueue';
import { usePieceStore } from './usePieceStore';

// Production-grade: All logic in database RPC functions
// No Edge Functions - everything uses RPC

interface FinalQCState {
    loading: boolean;
    error: string | null;
    
    qcInspectorPiece: (sampleId: string, pieceId: string, passed: boolean, defects?: Record<string, Record<string, number>>, notes?: string, date?: string) => Promise<void>;
    finalQCBulk: (qcResults: Array<{pieceId: string, passed: boolean, defects?: any}>) => Promise<void>;
}

export const useFinalQCStore = create<FinalQCState>((set) => ({
    loading: false,
    error: null,

    finalQCBulk: async (qcResults) => {
        if (!navigator.onLine) {
            await useOfflineQueue.getState().addToQueue('finalQCBulk', { qcResults });
            return;
        }
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;

        set({ loading: true, error: null });
        try {
            const idempotencyKey = `final_qc_${Date.now()}`;
            const qcJson = qcResults.map(r => ({
                piece_id: r.pieceId,
                passed: r.passed,
                defects: r.defects || []
            }));

            const { data, error } = await supabase.rpc('rpc_final_qc_bulk', {
                p_factory_id: u.factory_id,
                p_qc_results: qcJson,
                p_user_id: u.id,
                p_user_role: u.role,
                p_qc_at: new Date().toISOString(),
                p_idempotency_key: idempotencyKey
            });

            if (error) throw error;
            if (!data.success) throw new Error(data.error_message);

            console.log(`✅ Final QC completed: ${data.affected_count} pieces`);
            await usePieceStore.getState().fetchAssignedSamples();

        } catch (e: any) {
            console.error('❌ finalQCBulk:', e);
            set({ error: e.message });
            throw e;
        } finally {
            set({ loading: false });
        }
    },

    qcInspectorPiece: async (_sampleId, pieceId, passed, defects, notes, date) => {
        // Single-piece wrapper - uses bulk RPC internally
        const defectsArr = passed ? [] : (defects ? Object.entries(defects).flatMap(([cat, items]) =>
            Object.entries(items as Record<string, number>).map(([item, count]) => ({
                category: cat,
                item: item,
                count: count
            }))
        ) : []);
        
        const { finalQCBulk } = useFinalQCStore.getState();
        await finalQCBulk([{ pieceId, passed, defects: defectsArr }]);
    },
}));
